import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import {
  postRequest,
  setUserEmail,
  setUserType,
  showModalFunc,
  updateUser,
  hideModal,
  login,
} from "../../../redux/action";
import styles from "./signUp.module.css";
import { whiteEyeIcon, whiteCloseEyeIcon, logoWhiteImg } from "../../../images";
import InputWrapper from "../../inputWrapper";
import { useForm } from "react-hook-form";
import { FirstLastName } from "../../../styles/common";
import Button from "../../button";
import PasswordChecker from "../passwordChecker";
import { mergeGuestCards } from "../../../utils/installmentPayments";
import { storePendingVerificationEmail, storePendingVerificationUserType } from "../../../utils/verificationEmail";
import { storeScopedJwt, storePendingCheckoutCourseId } from "../../../utils/scopedAuth";
import { storeOriginalRegisteredRole } from "../../../utils/originalRegisteredRole";

const duplicateRegistrationMessages = {
  student:
    "This email is already used to register as a student. Please log in with your student account or reset your password.",
  mentor:
    "This email is already used to register as a mentor. Please log in with your mentor account or reset your password.",
};

const getDuplicateRegistrationMessage = (errorMsg = "") => {
  if (/mentor/i.test(errorMsg)) {
    return duplicateRegistrationMessages.mentor;
  }

  if (/student/i.test(errorMsg)) {
    return duplicateRegistrationMessages.student;
  }

  return duplicateRegistrationMessages.student;
};

const isDuplicateRegistrationError = (errorMsg = "") =>
  /already taken|already used|already registered|email is already taken|email already exists/i.test(
    errorMsg,
  );

const SignUp = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isDirty, isValid },
  } = useForm({ mode: "all", defaultValues: { phone_number: "+1" } });

  // Capture ?courseId= forwarded from checkout when an unauthenticated user
  // tries to pay. We persist it after registration so the verify-email page
  // can display a "Return to payment" deep-link.
  const checkoutCourseId = new URLSearchParams(location?.search).get('courseId');

  const passwordValue = watch("password", "");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleLoginClick = () => {
    // Dispatch actions to hide the signup modal and show the login modal
    dispatch(hideModal()); // Hide the signup modal
    dispatch(showModalFunc("student")); // Show the student login modal
  };

  const navigate = useNavigate();

  const onSubmit = async (formData) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const [success, responseData] = await postRequest(
        "/api/auth/local/register",
        {
          username: formData.firstname + formData.lastname,
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phone_number,
          originalRegisteredRole: "student",
        },
      );

      if (!success || responseData?.error) {
        // Enhanced error handling for common authentication issues
        if (responseData?.error) {
          const errorMsg =
            responseData.error.message || "An error occurred while signing up";

          // Check for email/username already taken error
          if (
            responseData.error.status === 500 ||
            isDuplicateRegistrationError(errorMsg)
          ) {
            const message = getDuplicateRegistrationMessage(errorMsg);
            setError("email", { type: "server", message });
            toast.error(message, { autoClose: 3000 });
          } else if (
            errorMsg.includes("email") ||
            errorMsg.toLowerCase().includes("email")
          ) {
            setError("email", { type: "server", message: errorMsg });
            toast.error(errorMsg, { autoClose: 2000 });
          } else if (
            errorMsg.includes("password") ||
            errorMsg.toLowerCase().includes("password")
          ) {
            setError("password", { type: "server", message: errorMsg });
            toast.error(errorMsg, { autoClose: 2000 });
          } else if (
            errorMsg.includes("phone") ||
            errorMsg.toLowerCase().includes("phone")
          ) {
            setError("phone_number", { type: "server", message: errorMsg });
            toast.error(errorMsg, { autoClose: 2000 });
          } else {
            toast.error(errorMsg, { autoClose: 2000 });
          }
        } else {
          toast.error("Registration failed. Please try again later.", {
            autoClose: 2000,
          });
        }
      } else {
        reset();
        storeOriginalRegisteredRole(formData.email, "student");
        const userEmail = storePendingVerificationEmail(formData.email);
        dispatch(setUserEmail(userEmail));
        dispatch(setUserType("user"));
        storePendingVerificationUserType("student");
        dispatch(updateUser(formData));

        if (responseData.paymentSessionToken) {
          storeScopedJwt(responseData.paymentSessionToken);
        }

        // Persist the course the user was trying to buy so the verify-email
        // page can display a "Return to payment" deep-link.  We check both
        // the query-param captured from the URL and a courseId the backend
        // might echo back in the registration response.
        const courseIdToStore = checkoutCourseId || responseData.courseId;
        if (courseIdToStore) {
          storePendingCheckoutCourseId(courseIdToStore);
        }

        // IMPORTANT: Merge guest cards after signup (if JWT token is returned)
        if (responseData.jwt) {
          try {
            await mergeGuestCards(responseData.jwt, userEmail);
          } catch (mergeError) {
            console.error("Failed to merge guest cards:", mergeError);
            // Don't block signup flow if merge fails
          }
        }

        // If user is already confirmed (e.g. existing mentor adding student access),
        // log them in directly instead of redirecting to verify-email
        if (responseData.user?.confirmed && responseData.jwt) {
          Cookies.set("token", responseData.jwt, {
            expires: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
          });
          dispatch(login({ ...responseData.user, token: responseData.jwt }));
          dispatch(hideModal());
          navigate("/dashboard");
          toast.success(responseData.message || "Account created successfully! Welcome!");
        } else {
          navigate("/verify-email");
          toast.success("Account created successfully! Please verify your email.");
        }
      }
    } catch (error) {
      console.error("Error during sign up:", error);
      toast.error(`An error occurred while signing up`, { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="bg-black min-h-screen">
      <div className={styles["authContentModal"]}>
        <div className={styles["headerWrapper"]}>
          <Link to={"/"} className={styles["imageWrapper"]}>
            <img
              src={logoWhiteImg}
              alt="logo"
              onClick={() => dispatch(hideModal())}
            />
          </Link>
          <h2 className={styles["authHeader"]}>Create Account</h2>
        </div>
        <div className={styles["signup_wrapper"]}>
          <div className={styles["inner_form"]}>
            <form
              className={styles["login_form"]}
              onSubmit={handleSubmit(onSubmit)}
            >
              <FirstLastName>
                <InputWrapper
                  title="First Name"
                  fieldName="firstname"
                  requiredMessage="Enter valid firstname"
                  register={register}
                  errors={errors}
                />
                <InputWrapper
                  title="Last Name"
                  fieldName="lastname"
                  requiredMessage="Enter valid lastname"
                  register={register}
                  errors={errors}
                />
              </FirstLastName>
              {/* <InputWrapper
                title="Username"
                type="text"
                fieldName="username"
                requiredMessage="Username is required"
                register={register}
                errors={errors}
              /> */}
              <InputWrapper
                title="Email"
                type="email"
                placeholder="Example@example.com"
                fieldName="email"
                requiredMessage="Email is required"
                patternValue={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
                patternMessage="Please enter a valid email adress. Example: name@gmail.com"
                compoundValidation={true}
                register={register}
                errors={errors}
              />
              <InputWrapper
                inputTag="Phone Input"
                title="Phone Number"
                fieldName="phone_number"
                requiredMessage="Phone number is required"
                compoundValidation={true}
                control={control}
                register={register}
                errors={errors}
              />
              <div className="mb-3">
                <InputWrapper
                  inputTag="Password"
                  title="Create Password"
                  type={showPassword ? "text" : "password"}
                  fieldName="password"
                  requiredMessage="Password is Required"
                  compoundValidation={true}
                  register={register}
                  errors={errors}
                  icon={
                    showPassword || !isDirty ? whiteEyeIcon : whiteCloseEyeIcon
                  }
                  onImageClick={handleShowPassword}
                  patternValue={
                    /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/
                  }
                  patternMessage="Low Password Strength"
                  className={"!text-[14px]"}
                />
                <PasswordChecker passwordValue={passwordValue} />
              </div>
              <InputWrapper
                inputTag="Password"
                title="Confirm Password"
                type={showPassword ? "text" : "password"}
                fieldName="confirmpassword"
                requiredMessage="Password is Required"
                compoundValidation={true}
                {...register("confirmpassword", {
                  validate: (value) =>
                    value === passwordValue ||
                    "The new password fields do not match. Please try again.",
                })}
                register={register}
                errors={errors}
                icon={
                  showPassword || !isDirty ? whiteEyeIcon : whiteCloseEyeIcon
                }
                onImageClick={handleShowPassword}
                className={"!text-[14px]"}
              />
              <label className="text-[#D8D8D8] font-normal text-xs leading-5 mb-[-.5rem]">
                By creating an account, you accept our{" "}
                <button
                  type="button"
                  onClick={() => {
                    dispatch(hideModal()); // Close the modal
                    window.open(
                      window.location.origin + "/terms-and-conditions",
                      "_blank",
                    );
                  }}
                  className="underline hover:text-[#F8D210] cursor-pointer bg-none border-none p-0 font-inherit text-inherit"
                >
                  Terms
                </button>
                .
              </label>
              <Button
                size="full"
                variant={isValid && !isLoading ? "secondary" : "disabled"}
                type="submit"
                className="mt-4 bg-[#D8D8D8]"
                disabled={!isValid || isLoading}
              >
                {isLoading ? "Creating account..." : "Create account"}
              </Button>
            </form>
            <label className="text-[#D8D8D8] font-normal text-xs leading-5 mt-8">
              Already have an account?
              <button
                type="button"
                className="underline ml-1 text-[#D8D8D8] bg-transparent border-none cursor-pointer text-xs leading-5"
                onClick={handleLoginClick}
              >
                Log in
              </button>
            </label>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignUp;
